import { sanitizeSoilMoisturePercent } from './soilMoistureSanitize';

// Map utility functions extracted from components

export async function reverseGeocode(lng: number, lat: number): Promise<string> {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  if (!token) {
    return '';
  }
  const lon = Number(lng);
  const la = Number(lat);
  if (!Number.isFinite(lon) || !Number.isFinite(la)) {
    return '';
  }
  // Mapbox returns 422 Unprocessable for out-of-range pairs (common with swapped lat/lng in stored data).
  if (la < -90 || la > 90 || lon < -180 || lon > 180) {
    return '';
  }
  // Omit `types` and `limit`: some combinations (e.g. multiple types, or types with no matches at a point)
  // return 422 from the v5 reverse endpoint; default reverse still includes place/region context we parse below.
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${la}.json?access_token=${encodeURIComponent(token)}`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      return '';
    }
    const data = await res.json();
    const features = data.features as
      | Array<{ id?: string; text?: string; context?: Array<{ id: string; text: string }> }>
      | undefined;
    if (!features?.length) {
      return '';
    }

    let district = '';
    let state = '';

    for (const feature of features) {
      if (feature.id?.startsWith('district.') && feature.text) {
        district = feature.text;
      }
      if (feature.id?.startsWith('region.') && feature.text) {
        state = feature.text;
      }
      for (const item of feature.context || []) {
        if (item.id.startsWith('district.')) district = item.text;
        if (item.id.startsWith('region.')) state = item.text;
      }
      if (district && state) {
        return `${district}, ${state}`;
      }
    }

    if (district && state) {
      return `${district}, ${state}`;
    }
    if (state) {
      return district ? `${district}, ${state}` : `Unknown County, ${state}`;
    }

    const f0 = features[0];
    const label = f0.text || '';
    let region = '';
    for (const item of f0.context || []) {
      if (item.id.startsWith('region.')) {
        region = item.text;
        break;
      }
    }
    if (label && region) {
      return `${label}, ${region}`;
    }
    return district || label || '';
  } catch {
    return '';
  }
}

export async function fetchSoilMoisture(
  lng: number,
  lat: number,
  headers?: Record<string, string>
): Promise<number | null> {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const url = `/api/precip-ai?endpoint=soil-moisture-daily&start=${weekAgo}&end=${today}&longitude=${lng}&latitude=${lat}&format=geojson`;
  try {
    const res = await fetch(url, headers && Object.keys(headers).length > 0 ? { headers } : undefined);
    if (!res.ok) return null;
    const data = await res.json();
    const days = data?.features?.[0]?.properties?.days;
    if (!Array.isArray(days) || days.length === 0) return null;
    for (let i = days.length - 1; i >= 0; i--) {
      const day = days[i] as Record<string, unknown> | undefined;
      if (!day) continue;
      const primary = day.soil_moisture;
      if (typeof primary === 'number' && !Number.isNaN(primary)) {
        const v = sanitizeSoilMoisturePercent(primary);
        if (v != null) return v;
      }
      const alt = day.rsm_0_10cm ?? day['rsm_0-10cm'];
      if (typeof alt === 'number' && !Number.isNaN(alt)) {
        const v = sanitizeSoilMoisturePercent(alt);
        if (v != null) return v;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export async function fetchSoilTemperature(
  lng: number,
  lat: number,
  headers?: Record<string, string>
): Promise<number | null> {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const url = `/api/precip-ai?endpoint=temp-0-10cm-hourly&start=${yesterday}&end=${today}&longitude=${lng}&latitude=${lat}&format=geojson`;
  try {
    const res = await fetch(url, headers && Object.keys(headers).length > 0 ? { headers } : undefined);
    if (!res.ok) return null;
    const data = await res.json();
    const hours = data.features?.[0]?.properties?.hours;
    if (Array.isArray(hours) && hours.length > 0) {
      for (let i = hours.length - 1; i >= 0; i--) {
        const h = hours[i] as Record<string, unknown>;
        const c = h["temp_0-10cm"] ?? h.temp_0_10cm ?? h.soil_temp;
        if (typeof c === "number" && !Number.isNaN(c)) return c;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export async function fetchLastRainEvent(
  lng: number,
  lat: number,
  headers?: Record<string, string>
): Promise<any> {
  const url = `/api/precip-ai?endpoint=recent-rain&longitude=${lng}&latitude=${lat}&format=geojson`;
  try {
    const res = await fetch(url, headers && Object.keys(headers).length > 0 ? { headers } : undefined);
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Failed to load last rain event:', errorText);
      return null;
    }
    const data = await res.json();
    if (data.features && data.features.length > 0 && data.features[0].properties) {
      return data.features[0].properties;
    }
    return null;
  } catch (err) {
    console.error('Error loading last rain event:', err);
    return null;
  }
}

/** USGS EPQS NODATA sentinel (outside coverage or invalid). */
const USGS_EPQS_NO_DATA = -1000000;

function parseElevationNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

async function fetchUsgsElevationMeters(
  lng: number,
  lat: number,
  timeoutMs: number
): Promise<number | null> {
  const url = `https://epqs.nationalmap.gov/v1/json?x=${lng}&y=${lat}&units=Meters&output=json`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    const data = await res.json();
    const v = parseElevationNumber(data?.value);
    if (v == null) return null;
    if (v === USGS_EPQS_NO_DATA || v < -500 || v > 9000) return null;
    return v;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

function isPlausibleElevationMeters(m: number): boolean {
  return m > -500 && m < 9000;
}

/** Map labels often anchor beside summits; ±~180 m cross captures ridge crest for EPQS. */
const USGS_NEIGHBOR_RADIUS_METERS = 180;

function metersToLonLatDelta(latitude: number, metersEast: number, metersNorth: number): {
  dLng: number;
  dLat: number;
} {
  const latRad = (latitude * Math.PI) / 180;
  const mPerDegLat = 111_320;
  const dLat = metersNorth / mPerDegLat;
  const dLng = metersEast / (mPerDegLat * Math.cos(latRad));
  return { dLng, dLat };
}

/** Max EPQS reading at center + cardinal offsets — improves peaks when POI coords sit downslope. */
async function fetchUsgsElevationNeighborhoodMaxMeters(
  lng: number,
  lat: number,
  timeoutMs: number,
): Promise<number | null> {
  const R = USGS_NEIGHBOR_RADIUS_METERS;
  const card = metersToLonLatDelta(lat, R, 0);
  const orth = metersToLonLatDelta(lat, 0, R);
  const samples: readonly [number, number][] = [
    [lng, lat],
    [lng + card.dLng, lat],
    [lng - card.dLng, lat],
    [lng, lat + orth.dLat],
    [lng, lat - orth.dLat],
  ];
  const readings = await Promise.all(
    samples.map(([lo, la]) => fetchUsgsElevationMeters(lo, la, timeoutMs)),
  );
  const vals = readings.filter(
    (v): v is number => v != null && isPlausibleElevationMeters(v),
  );
  if (vals.length === 0) return null;
  return Math.max(...vals);
}

/**
 * Elevation from Mapbox Terrain-DEM raster (same token as the map). Browser-safe CORS.
 * @see https://docs.mapbox.com/data/tilesets/guides/access-elevation-data/
 */
function decodeMapboxTerrainRgb(r: number, g: number, b: number): number {
  return -10000 + (r * 256 * 256 + g * 256 + b) * 0.1;
}

function lngLatToTilePixel(lng: number, lat: number, z: number): {
  tileX: number;
  tileY: number;
  px: number;
  py: number;
} {
  const scale = 2 ** z;
  const worldSize = scale * 256;
  const x = ((lng + 180) / 360) * worldSize;
  const latRad = (lat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * worldSize;
  const tileX = Math.floor(x / 256);
  const tileY = Math.floor(y / 256);
  const px = Math.min(255, Math.max(0, Math.floor(x - tileX * 256)));
  const py = Math.min(255, Math.max(0, Math.floor(y - tileY * 256)));
  return { tileX, tileY, px, py };
}

async function fetchElevationFromMapboxTerrainDem(
  lng: number,
  lat: number,
  zoom: number
): Promise<number | null> {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  if (!token || typeof document === 'undefined') return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const { tileX, tileY, px, py } = lngLatToTilePixel(lng, lat, zoom);
  const url = `https://api.mapbox.com/v4/mapbox.mapbox-terrain-dem-v1/${zoom}/${tileX}/${tileY}.png?access_token=${encodeURIComponent(token)}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(url, { signal: controller.signal, mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const bmp = await createImageBitmap(blob);
    const tw = bmp.width;
    const th = bmp.height;
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      bmp.close?.();
      return null;
    }
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();
    const sx = Math.min(tw - 1, Math.floor((px / 256) * tw));
    const sy = Math.min(th - 1, Math.floor((py / 256) * th));
    const { data } = ctx.getImageData(sx, sy, 1, 1);
    const elev = decodeMapboxTerrainRgb(data[0], data[1], data[2]);
    if (!Number.isFinite(elev) || elev < -500 || elev > 9000) return null;
    return elev;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Same-origin API (dev proxy + Vercel) — USGS + OpenTopoData on the server. */
async function fetchElevationFromApiProxy(
  lng: number,
  lat: number,
  timeoutMs: number
): Promise<number | null> {
  // Round to 4dp (~11 m). Raw float coordinates make every tap a unique URL, so
  // the endpoint's Cache-Control can never be hit — neither by the browser nor
  // by the CDN. Rounding is what makes that header reachable at all.
  const params = new URLSearchParams({
    lng: String(Math.round(lng * 1e4) / 1e4),
    lat: String(Math.round(lat * 1e4) / 1e4),
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`/api/elevation?${params.toString()}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    const v = parseElevationNumber(data?.elevationMeters);
    if (v == null || !isPlausibleElevationMeters(v)) return null;
    return v;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Elevation in meters (WGS84 point).
 *
 * USGS National Map EPQS (3DEP) is preferred — max over center + ~180 m cardinal samples so
 * label anchors downslope from summits still land near published peak elevations.
 *
 * If the browser cannot reach EPQS (e.g. CSP `connect-src` omitting nationalmap.gov), we call
 * same-origin `/api/elevation` next so the server can still query USGS before using Mapbox.
 *
 * Mapbox Terrain-DEM runs in parallel with client USGS for latency; it is only used after
 * USGS and the API proxy both miss.
 *
 * Name is historical — not Mapbox Tilequery.
 */
/**
 * 4dp ≈ 11 m, finer than this lookup can distinguish. Taps a few metres apart
 * share a bucket instead of each starting their own fan-out.
 */
const ELEVATION_CACHE_PRECISION = 1e4;

/** Keyed on the rounded point, holding the promise so concurrent taps join it. */
const elevationCache = new Map<string, Promise<number | null>>();

/**
 * Entries are tiny and only added on user action - a tapped point, a saved
 * favourite - so this is a bound rather than a fix for observed growth. Map
 * insertion order is the eviction order, which is close enough to LRU here
 * because a re-looked-up point is served from the cache without reordering.
 */
const ELEVATION_CACHE_MAX_ENTRIES = 500;

function rememberElevation(key: string, value: Promise<number | null>) {
  elevationCache.set(key, value);
  while (elevationCache.size > ELEVATION_CACHE_MAX_ENTRIES) {
    const oldest = elevationCache.keys().next();
    if (oldest.done) break;
    elevationCache.delete(oldest.value);
  }
}

const elevationCacheKey = (lng: number, lat: number) =>
  `${Math.round(lng * ELEVATION_CACHE_PRECISION) / ELEVATION_CACHE_PRECISION},` +
  `${Math.round(lat * ELEVATION_CACHE_PRECISION) / ELEVATION_CACHE_PRECISION}`;

async function resolveElevation(lng: number, lat: number): Promise<number | null> {
  const usgsPromise = fetchUsgsElevationNeighborhoodMaxMeters(lng, lat, 5000);
  const mapboxPromise = (async () => {
    return (
      (await fetchElevationFromMapboxTerrainDem(lng, lat, 14)) ??
      (await fetchElevationFromMapboxTerrainDem(lng, lat, 12))
    );
  })();

  const [usgs, mapbox] = await Promise.all([usgsPromise, mapboxPromise]);
  if (usgs != null) return usgs;

  const proxy = await fetchElevationFromApiProxy(lng, lat, 8000);
  if (proxy != null) return proxy;
  if (mapbox != null) return mapbox;

  return null;
}

export async function fetchElevationFromTilequery(lng: number, lat: number): Promise<number | null> {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const key = elevationCacheKey(lng, lat);
  const cached = elevationCache.get(key);
  if (cached) return cached;

  const pending = resolveElevation(lng, lat);
  rememberElevation(key, pending);

  try {
    const elevation = await pending;
    // A miss is a transient upstream failure, not a fact about the point, so it
    // is dropped and the next caller retries. Only a real reading is kept.
    if (elevation == null) elevationCache.delete(key);
    return elevation;
  } catch (error) {
    elevationCache.delete(key);
    throw error;
  }
}

export function getStateRegion(stateName: string): string {
  const northeast = ['Connecticut', 'Maine', 'Massachusetts', 'New Hampshire', 'Rhode Island', 'Vermont', 'New Jersey', 'New York', 'Pennsylvania', 'Delaware', 'Maryland'];
  const south = ['Alabama', 'Arkansas', 'Florida', 'Georgia', 'Kentucky', 'Louisiana', 'Mississippi', 'North Carolina', 'South Carolina', 'Tennessee', 'Texas', 'Virginia', 'West Virginia'];
  const midwest = ['Illinois', 'Indiana', 'Iowa', 'Kansas', 'Michigan', 'Minnesota', 'Missouri', 'Nebraska', 'North Dakota', 'Ohio', 'South Dakota', 'Wisconsin'];
  const west = ['Alaska', 'Arizona', 'California', 'Colorado', 'Hawaii', 'Idaho', 'Montana', 'Nevada', 'New Mexico', 'Oregon', 'Utah', 'Washington', 'Wyoming'];
  
  if (northeast.includes(stateName)) return 'Northeast';
  if (south.includes(stateName)) return 'South';
  if (midwest.includes(stateName)) return 'Midwest';
  if (west.includes(stateName)) return 'West';
  return 'Other';
}

